import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import debug from 'debug';
import { useCallback, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Avatar } from '@/components/Avatar/Avatar';
import { WebviewResponse } from '@/components/Message/Webview/WebviewResponse';
import { useSocket } from '@/hooks/useSocket';
import { useTrackMessageAnalytics, } from '@/hooks/useTrackMessageAnalytics';
import { maxReferencesSelector } from '@/state/config';
import { isResponseDisabledSelector, showExternalUrlsSelector, showMessageFeedbackSelector, } from '@/state/session';
import { sleep } from '@/util/utils';
import { Audio } from '../Audio';
import { Carousel } from '../Carousel/Carousel';
import { FeedbackMessage } from '../FeedbackMessage/FeedbackMessage';
import { File } from '../File';
import { Image } from '../Image';
import { Options } from '../Options/Options';
import { References } from '../References/References';
import { ResponseWrapper } from '../ResponseWrapper/ResponseWrapper';
import { Survey } from '../Survey';
import { Text } from '../Text';
import { Url } from '../Url';
import { Video } from '../Video';
import styles from './BrainResponseEvent.module.scss';
const log = debug('moveo:response-event');
export const BrainResponseEvent = ({ event }) => {
    const { sendMessageFeedback, sendMessage } = useSocket();
    const showFeedBackBubble = useRecoilValue(showMessageFeedbackSelector(event.event_id));
    const { trackMessageClick } = useTrackMessageAnalytics();
    const maxReferences = useRecoilValue(maxReferencesSelector);
    const shouldShowExternalUrls = useRecoilValue(showExternalUrlsSelector(event.event_id));
    const [isVoteComplete, setIsVoteComplete] = useState(false);
    const isDisabled = useRecoilValue(isResponseDisabledSelector(event.event_id));
    const message = event.response;
    const [clicked, setClicked] = useState(null);
    const handleReactionClick = useCallback(async (value) => {
        trackMessageClick(event, {
            type: 'reaction',
            value,
        });
        const request_id = event === null || event === void 0 ? void 0 : event.request_id;
        setClicked(value);
        sendMessageFeedback(request_id, value);
        await sleep(1000);
        setIsVoteComplete(true);
    }, [event, sendMessageFeedback, trackMessageClick]);
    const handleButtonClick = (data) => {
        trackMessageClick(event, data);
    };
    const renderResponse = () => {
        var _a, _b;
        switch (message.type) {
            case 'text': {
                const external_urls = (_b = (_a = event === null || event === void 0 ? void 0 : event.collection) === null || _a === void 0 ? void 0 : _a.external_urls) === null || _b === void 0 ? void 0 : _b.filter((url) => !!url.name && !!url.external_url);
                return (_jsxs(ResponseWrapper, { children: [_jsx(Avatar, { avatar: event.author_avatar }), _jsx(Text, { message: message, disabled: isDisabled, onClick: handleButtonClick }), maxReferences && shouldShowExternalUrls && (_jsx(References, { urls: external_urls, maxReferences: maxReferences, onClick: handleButtonClick }))] }));
            }
            case 'url':
                return (_jsxs(ResponseWrapper, { children: [_jsx(Avatar, { avatar: event.author_avatar }), _jsx(Url, { avatar: event.author_avatar, url: message.url, onClick: handleButtonClick })] }));
            case 'video':
                return (_jsxs(ResponseWrapper, { children: [_jsx(Avatar, { avatar: event.author_avatar }), _jsx(Video, { url: message.url, name: message.name })] }));
            case 'image':
                return (_jsxs(ResponseWrapper, { children: [_jsx(Avatar, { avatar: event.author_avatar }), _jsx(Image, { url: message.url, name: message.name })] }));
            case 'file':
                return (_jsxs(ResponseWrapper, { children: [_jsx(Avatar, { avatar: event.author_avatar }), _jsx(File, { url: message.url, name: message.name })] }));
            case 'audio':
                return (_jsxs(ResponseWrapper, { children: [_jsx(Avatar, { avatar: event.author_avatar }), _jsx(Audio, { avatar: event.author_avatar, url: message.url })] }));
            case 'carousel':
                return (_jsx(Carousel, { avatar: event.author_avatar, message: message, disabled: isDisabled, onButtonClick: handleButtonClick }));
            case 'pause':
                return null;
            case 'webview':
                return (_jsx(WebviewResponse, { onButtonClick: handleButtonClick, avatar: event.author_avatar, webview: message }));
            case 'survey':
                return (_jsx(Survey, { onButtonClick: handleButtonClick, avatar: event.author_avatar, survey: message }));
            default:
                log('message-not-supported', message);
        }
        return null;
    };
    const handleOptionClick = useCallback((text, label) => {
        trackMessageClick(event, { type: 'option', value: text, label });
        sendMessage({ text, label });
    }, [event, sendMessage, trackMessageClick]);
    const hasOptions = 'options' in message;
    return (_jsxs("div", { className: cn(styles.brainMessageContainer, {
            [styles.showFeedBackBubble]: showFeedBackBubble,
        }), children: [renderResponse(), message && hasOptions && (_jsx(Options, { options: message.options, disabled: isDisabled, handleOptionClick: handleOptionClick })), showFeedBackBubble && !isVoteComplete && (_jsx(FeedbackMessage, { clicked: clicked, handleClick: handleReactionClick }))] }));
};
